import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import Cookies from "js-cookie";
import utils from "../assets/js/utils";
Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    status: "",
    membership: 0,
    token: localStorage.getItem("token") || "",
    name: "",
    enabled: false,
    // WebSocket state
    socket: {
      isConnected: false,
      message: '',
      reconnectError: false,
    },
    // Auction state
    liveBidCode: null,
    connectedUsers: 0,
  },
  mutations: {
    auth_request(state) {
      state.status = "loading";
    },
    auth_success(state, authData) {
      state.status = "success";
      state.membership = parseInt(authData.membership);
      state.token = authData.token;
      state.name = authData.name;
      state.enabled = Boolean(authData.enabled);
    },
    auth_error(state) {
      state.status = "error";
    },
    logout(state) {
      state.status = "";
      state.membership = 0;
      state.token = "";
      state.name = "";
      state.enabled = false;
    },
    auth_refresh_token(state, token) {
      state.status = "refresh";
      state.token = token;
    },
    // WebSocket mutations
    SOCKET_ONOPEN(state, event) {
      Vue.prototype.$socket = event.currentTarget;
      state.socket.isConnected = true;
      console.log('🔌 WebSocket connected');
    },
    SOCKET_ONCLOSE(state) {
      state.socket.isConnected = false;
      console.log('🔌 WebSocket disconnected');
    },
    SOCKET_ONERROR(state, event) {
      console.error('🔌 WebSocket error:', event);
    },
    SOCKET_ONMESSAGE(state, message) {
      state.socket.message = message;
      // Handle auction-specific messages
      if (message.message && message.message.action) {
        switch (message.message.action) {
          case "auth":
            break;
          case "pingback":
            break;
          case "auctionStarts":
            console.log('🚀 Auction starts:', message.message.carCode);
            state.liveBidCode = message.message.carCode;
            break;
          case "auctionEnds":
            console.log('🏁 Auction ends:', message.message.carCode);
            // Clear live bid after delay
            setTimeout(() => {
              state.liveBidCode = null;
            }, (message.message.betweenTime || 0) + 1000);
            break;
          case "userChange":
            state.connectedUsers = message.message.users;
            break;
          case "auctionBump":
            console.log('💰 Auction bid bump:', message.message.carCode, message.message.bid);
            break;
          case "bid":
            console.log('💵 New bid placed:', message.message.carCode, message.message.bid);
            break;
          default:
        }
      }
    },
    SOCKET_RECONNECT(state, count) {
      console.info('🔌 WebSocket reconnecting, attempt:', count);
    },
    SOCKET_RECONNECT_ERROR(state) {
      state.socket.reconnectError = true;
      console.error('🔌 WebSocket reconnection failed');
    },
    // Auction-specific mutations
    SET_LIVE_BID_CODE(state, code) {
      state.liveBidCode = code;
    },
    CLEAR_LIVE_BID_CODE(state) {
      state.liveBidCode = null;
    },
    SET_CONNECTED_USERS(state, count) {
      state.connectedUsers = count;
    },
  },
  actions: {
    login: function({ commit }, { membership, password }) {
      commit("auth_request");
      return new Promise((resolve, reject) => {
        Vue.axios
          .post("/fts/users/authenticate", {
            code: membership,
            password: password,
          })
          .then((resp) => {
            if (resp.data.auth) {
              commit("auth_success", {
                membership: resp.data.code,
                token: resp.data.token,
                name: resp.data.name,
                enabled: resp.data.enabled,
              });
              Vue.prototype.axios.defaults.headers.common["Authorization"] =
                "Bearer " + resp.data.token;

              utils.connectWebSocket(resp.data.token, Vue);

              console.log('👋 User logged in successfully:', resp.data.code);
              resolve("ok");
              return;
            }

            commit("auth_error");
            reject("Credenciales inválidas");
          })
          .catch(() => {
            commit("auth_error");
            reject(
              "Error general.  Por favor vuelva a intentar en unos minutos."
            );
          });
      });
    },
    logout: function({ commit }) {
      return new Promise((resolve) => {
        // Properly disconnect WebSocket and notify server
        utils.disconnectWebSocket(Vue);
        
        // Clear authentication headers
        delete Vue.prototype.axios.defaults.headers.common["Authorization"];
        
        // Clear state
        commit("logout");
        
        console.log('👋 User logged out successfully');
        resolve();
      });
    },
    refreshToken: function({ commit }, token) {
      return new Promise((resolve) => {
        Vue.prototype.axios.defaults.headers.common["Authorization"] =
          "Bearer " + token;
        commit("auth_refresh_token", token);
        resolve();
      });
    },
    reconnectWebSocket: function() {
      return new Promise((resolve) => {
        try {
          console.log('🔌 Store: Attempting WebSocket reconnection...');
          utils.connectWebSocket(this.state.token, Vue);
          console.log('✅ Store: WebSocket reconnection successful');
          resolve();
        } catch (error) {
          console.error('❌ Store: WebSocket reconnection failed:', error);
          // Don't reject - just resolve to prevent blocking the app
          resolve();
        }
      });
    },
    // WebSocket actions
    sendMessage: function(context, message) {
      if (Vue.prototype.$socket && Vue.prototype.$socket.readyState === WebSocket.OPEN) {
        Vue.prototype.$socket.send(JSON.stringify(message));
      }
    },
    sendObject: function(context, obj) {
      if (Vue.prototype.$socket && Vue.prototype.$socket.readyState === WebSocket.OPEN) {
        Vue.prototype.$socket.send(JSON.stringify(obj));
      }
    },
  },
  getters: {
    isLoggedIn: function(state) {
      return state.status === "success" || state.status === "refresh";
    },
    loggedInMembership: function(state) {
      return state.membership;
    },
    loggedInName: function(state) {
      return state.name;
    },
    isEnabled: function(state) {
      return state.enabled;
    },
    token: function(state) {
      return state.token;
    },
    // WebSocket getters
    isSocketConnected: function(state) {
      return state.socket.isConnected;
    },
    socketMessage: function(state) {
      return state.socket.message;
    },
    liveBidCode: function(state) {
      return state.liveBidCode;
    },
    connectedUsers: function(state) {
      return state.connectedUsers;
    },
  },
  plugins: [
    createPersistedState({
      storage: {
        getItem: (key) => Cookies.get(key),
        setItem: (key, value) => {
          Cookies.set(key, value, { expires: 1 / 48, secure: false });
        },
        removeItem: (key) => Cookies.remove(key),
      },
    }),
  ],
});
